import { useState } from 'react';
import { theme } from '@frontend/theme';
import { Button, Text, DayPicker, DayPickerDays } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { BreakSchedulesByDay } from './phone-hours-form';
import type { Break, DaysOfWeek } from './use-phone-hours-form';

export const hasBreakConflict = (breakA: Break, breakB: Break) => {
  if (breakA.id === breakB.id) {
    return false; // Same break (don't consider as conflict)
  }

  const [startA, endA] = [breakA.startTime, breakA.endTime];
  const [startB, endB] = [breakB.startTime, breakB.endTime];
  return (
    (startA < endB && endA > startB) || // Overlapping intervals
    (startB < endA && endB > startA)
  );
};

export const BreakDuplicator = ({
  title,
  primary,
  secondary,
  breakSchedulesByDay,
  day,
}: {
  title: string;
  primary: { label: string; onClick: (selectedDays: DaysOfWeek[]) => void };
  secondary: { label: string; onClick: () => void };
  breakSchedulesByDay: BreakSchedulesByDay;
  day: DaysOfWeek;
}) => {
  const schedule = { ...breakSchedulesByDay };
  delete schedule[day];

  return (
    <HourDuplicator title={title} primary={primary} secondary={secondary} disabledDays={[day]} selectedDays={[day]} />
  );
};

export const HourDuplicator = ({
  title,
  disabledDays,
  primary,
  secondary,
  selectedDays: _selectedDays = [],
}: {
  title: string;
  disabledDays?: DaysOfWeek[];
  primary?: {
    label: string;
    onClick: (selectedDays: DaysOfWeek[]) => void;
  };
  secondary?: {
    label: string;
    onClick: () => void;
  };
  selectedDays?: DaysOfWeek[];
}) => {
  const [selectedDays, setSelectedDays] = useState<DaysOfWeek[]>(_selectedDays);

  return (
    <div
      css={{
        borderRadius: theme.borderRadius.medium,
        background: theme.colors.indigo5,
        border: `1px solid ${theme.colors.indigo10}`,
        padding: theme.spacing(2),

        display: 'grid',
        gap: theme.spacing(1),
      }}
    >
      <Text as='span' size='medium'>
        {title}
      </Text>
      <DayPicker
        selectedDays={_selectedDays}
        config={{
          options: DayPickerDays.map(({ label, value, disabled }) => ({
            label,
            value,
            disabled: disabled || disabledDays?.includes(value),
          })),
          onDayToggle: ({ selectedDays }) => {
            setSelectedDays(selectedDays);
          },
        }}
      />
      <div css={{ justifySelf: 'end' }}>
        {secondary && (
          <Button
            variant='tertiary'
            trackingId={trackingId({ context: 'setting', feature: 'phone-hours', details: 'cancel-duplicate-hours' })}
            onClick={secondary.onClick}
          >
            {secondary.label}
          </Button>
        )}
        {primary && (
          <Button
            variant='secondary'
            trackingId={trackingId({ context: 'setting', feature: 'phone-hours', details: 'apply-duplicate-hours' })}
            onClick={() => primary.onClick(selectedDays)}
          >
            {primary.label}
          </Button>
        )}
      </div>
    </div>
  );
};

export const DuplicateHoursButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => {
  return (
    <Button
      disabled={disabled}
      name='duplicate-hours'
      iconName='copy-small'
      variant='secondary'
      css={{ width: 40 }}
      onClick={onClick}
      trackingId={trackingId({ context: 'setting', feature: 'phone-hours', details: 'open-duplicate-hours' })}
    />
  );
};
